import { supabase } from '../lib/supabase'
import {
  applyFilters,
  applyOrdering,
  applyRange,
  handleQuery,
  formatSupabaseError,
  accessDeniedResponse,
  resolveRoleContext
} from './utils'
import { ensureCanManageEvent } from './utils'

const TABLE_NAME = 'orientation_registrations'
const RPC_CLAIM_BY_EMAIL = 'claim_orientation_registrations_by_email'
const RPC_HANDLE_SESSION_UPDATE = 'handle_orientation_session_update'

/**
 * Claim orientation registrations for the current user by matching email.
 * Call when a student logs in so dashboard can show "incomplete registrations".
 * Requires authenticated user.
 */
export const claimOrientationRegistrationsByEmail = async () => {
  const { error } = await supabase.rpc(RPC_CLAIM_BY_EMAIL)
  if (error) {
    console.warn('[orientationRegistrationsService] claim by email failed:', error)
    return { error: formatSupabaseError(error, 'Unable to link registrations.') }
  }
  return { error: null }
}

/**
 * List orientation registrations with optional filters and role constraints.
 * Agents see their events'; students see their own (student_id = auth).
 *
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   eventIds?: string[],
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number },
 *   completedFullRegistration?: boolean,
 *   studentId?: string
 * }} [options]
 */
export const listOrientationRegistrations = async (options = {}) => {
  const {
    role,
    userId,
    eventIds,
    filters,
    orderBy = { column: 'registered_at', ascending: false },
    range,
    completedFullRegistration,
    studentId
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  let query = supabase
    .from(TABLE_NAME)
    .select(`
      id, event_id, student_id, lead_id, email, full_name, phone, status,
      registered_at, completed_full_registration, created_at, updated_at,
      event:events(id, title, agent_id, has_free_orientation, orientation_session_id)
    `, { count: 'exact' })

  if (resolvedRole === 'student') {
    query = query.eq('student_id', resolvedUserId)
  }

  if (eventIds && eventIds.length > 0) {
    query = query.in('event_id', eventIds)
  }

  if (completedFullRegistration !== undefined) {
    query = query.eq('completed_full_registration', completedFullRegistration)
  }

  if (studentId !== undefined) {
    query = query.eq('student_id', studentId)
  }

  query = applyFilters(query, filters)
  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  return handleQuery(query, { fallbackMessage: 'Unable to load orientation registrations.' })
}

/**
 * Get a single orientation registration by id.
 *
 * @param {string} id
 * @param {{ role?: string, userId?: string, eventIds?: string[] }} [options]
 */
export const getOrientationRegistrationById = async (id, options = {}) => {
  const { role, userId, eventIds } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  let query = supabase
    .from(TABLE_NAME)
    .select(`
      *,
      event:events(id, title, agent_id, has_free_orientation, orientation_session_id)
    `)
    .eq('id', id)
    .maybeSingle()

  const result = await handleQuery(query, {
    fallbackMessage: 'Unable to load orientation registration.',
    allowEmpty: false
  })

  if (result.error || !result.data) {
    return result
  }

  if (resolvedRole === 'student' && result.data.student_id !== resolvedUserId) {
    return accessDeniedResponse('You can only view your own orientation registrations.')
  }

  if (resolvedRole === 'agent' && result.data.event?.agent_id !== resolvedUserId) {
    return accessDeniedResponse('You can only view orientation registrations for your events.')
  }

  return result
}

/**
 * Create an orientation registration (typically done by capture_early_lead RPC).
 * Used by service layer when needed.
 *
 * @param {Record<string, any>} payload
 * @param {{ role?: string, userId?: string }} [options]
 */
export const createOrientationRegistration = async (payload, options = {}) => {
  const { role, userId } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  if (resolvedRole === 'student') {
    return accessDeniedResponse('Students cannot create orientation registrations.')
  }

  if (payload?.event_id && resolvedRole !== 'admin') {
    const access = await ensureCanManageEvent(payload.event_id)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const normalized = {
    event_id: payload.event_id,
    student_id: payload.student_id ?? null,
    lead_id: payload.lead_id,
    email: payload.email ?? '',
    full_name: payload.full_name ?? null,
    phone: payload.phone ?? null,
    status: payload.status ?? 'active',
    completed_full_registration: payload.completed_full_registration ?? false
  }

  const query = supabase.from(TABLE_NAME).insert(normalized).select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to create orientation registration.',
    allowEmpty: false
  })
}

/**
 * Update an orientation registration.
 *
 * @param {string} id
 * @param {Record<string, any>} updates
 * @param {{ role?: string, userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updateOrientationRegistration = async (id, updates, options = {}) => {
  const { role, userId, eventId, eventIds } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student') {
    return accessDeniedResponse('Students cannot update orientation registrations.')
  }

  const scopedEventId = eventId || updates?.event_id
  if (scopedEventId && (resolvedRole === 'agent' || resolvedRole === 'partner')) {
    const access = await ensureCanManageEvent(scopedEventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const allowed = ['status', 'completed_full_registration', 'student_id', 'updated_at']
  const normalized = {}
  for (const key of allowed) {
    if (updates[key] !== undefined) {
      normalized[key] = updates[key]
    }
  }

  if (Object.keys(normalized).length === 0) {
    return { data: null, error: { message: 'No valid fields to update.', code: 'invalid_input' } }
  }

  let query = supabase.from(TABLE_NAME).update(normalized).eq('id', id)

  if (resolvedRole === 'agent' && !eventIds?.length) {
    query = query.eq('event_id', scopedEventId)
  }

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update orientation registration.',
    allowEmpty: false
  })
}

/**
 * Handle orientation session time/date change.
 * Cancels pending reminder emails, sends update email, queues new reminders.
 * Call when an orientation session's start_time or end_time is updated.
 *
 * @param {string} sessionId - UUID of the event_session that was updated
 * @returns {Promise<{ data: { ok: boolean, affected: number } | null, error: object | null }>}
 */
export const handleOrientationSessionUpdate = async (sessionId) => {
  if (!sessionId) {
    return { data: null, error: { message: 'Session ID is required.', code: 'invalid_input' } }
  }
  const { data, error } = await supabase.rpc(RPC_HANDLE_SESSION_UPDATE, { p_session_id: sessionId })
  if (error) {
    return { data: null, error: formatSupabaseError(error, 'Unable to reschedule orientation emails.') }
  }
  return { data: data ?? null, error: null }
}
